import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from './ThemeContext';
import './App.css';

// Transform raw product data to api_ads table format
function transformProductDataToAds(rawProduct) {
  const currentTime = new Date().toISOString().slice(0, 19).replace('T', ' ');

  // Extract data from raw product and transform to ads format
  const adsData = {
    id: (rawProduct.Id || '').replace(/^product_?/, ''), // Remove "product_" prefix
    adid: null, // Auto-increment (handled by DB)
    adtype: 3, // 1=image, 2=video, 3=product (default: 3 for products)
    adtypename: 'product', // Default to "product" since we're working with products data
    campaign_id: '', // Editable field, leave blank initially
    campgn_type: 1, // Default to 1 for "sponsored"
    campgn_typename: 'sponsored', // Default to "sponsored"
    adname: rawProduct.CampaignName || '',
    adtitle: (rawProduct.Name || '').substring(0, 45),
    addesc: (rawProduct.Description || '').substring(0, 75),
    adlandingurl: rawProduct.Url || '',
    aditemurl: rawProduct.ImageUrl || '',
    aditemurlid: null,
    cat_idctgfatherid: '', // Editable field
    cat_fathername: (rawProduct.Category || '').split(' > ')[0] || '',
    adbgttype: 1, // Default to 1 for "lifetime"
    adbgtname: 'lifetime', // Default to "lifetime"
    ad_bgtamt: '', // Editable field
    bidtype: 1, // Default to 1 for "click"
    bidname: 'click', // Default to "click"
    bidamount: '', // Editable field
    ad_startdate: '', // Editable field
    ad_enddate: '', // Editable field
    ad_status: 1, // Default to 1 for "active"
    ad_apprstatus: '', // Editable field
  };

  return adsData;
}

function AdsUpload() {
  const navigate = useNavigate();
  const { theme, toggleTheme, isDark } = useTheme();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(50); // Page size state, max 500
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render key

  // Row selection state for bulk operations
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Bulk operation states
  const [bulkCampaignId, setBulkCampaignId] = useState('');
  const [bulkAdTypeName, setBulkAdTypeName] = useState('');
  const [bulkCampgnTypename, setBulkCampgnTypename] = useState('');
  const [bulkCatIdctgfatherid, setBulkCatIdctgfatherid] = useState('');
  const [bulkAdbgtname, setBulkAdbgtname] = useState('');
  const [bulkAdBgtamt, setBulkAdBgtamt] = useState('');
  const [bulkBidname, setBulkBidname] = useState('');
  const [bulkBidamount, setBulkBidamount] = useState('');
  const [bulkAdStartdate, setBulkAdStartdate] = useState('');
  const [bulkAdEnddate, setBulkAdEnddate] = useState('');
  const [bulkAdStatus, setBulkAdStatus] = useState('');
  const [bulkAdApprstatus, setBulkAdApprstatus] = useState('');

  useEffect(() => {
    // Get ads data from sessionStorage (set by Products.js)
    try {
      const adsData = sessionStorage.getItem('adsFromProducts');
      if (adsData) {
        const parsedData = JSON.parse(adsData);
        // Transform raw product data to ads format
        const transformedData = parsedData.map(rawProduct => transformProductDataToAds(rawProduct));
        console.log('Transformed ads:', transformedData.slice(0, 3)); // Debug first 3 items
        setAds(transformedData);
        setTotalPages(Math.ceil(transformedData.length / pageSize)); // Using dynamic page size
      } else {
        setError('No ads data found. Please go back and click "Ads Upload Table" first.');
      }
    } catch (err) {
      console.error('Error accessing sessionStorage:', err);
      setError('Error loading ads data. Please try again.');
    }
  }, []);

  // Update select all state when page changes
  React.useEffect(() => {
    updateSelectAllState();
  }, [currentPage, ads, selectedRows]);

  const handleUpdateCampaignId = (index, newCampaignId) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].campaign_id = newCampaignId;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateAdTypeName = (index, newAdTypeName) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      
      // Update adtypename
      updatedAds[globalIndex].adtypename = newAdTypeName;
      
      // Sync adtype based on adtypename
      switch (newAdTypeName) {
        case 'image':
          updatedAds[globalIndex].adtype = 1;
          break;
        case 'video':
          updatedAds[globalIndex].adtype = 2;
          break;
        case 'product':
          updatedAds[globalIndex].adtype = 3;
          break;
        default:
          updatedAds[globalIndex].adtype = 3; // Default to product
      }
      
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateAdTitle = (index, newAdTitle) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].adtitle = newAdTitle;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateAdDesc = (index, newAdDesc) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].addesc = newAdDesc;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateCatIdctgfatherid = (index, newCatId) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].cat_idctgfatherid = newCatId;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateCatFathername = (index, newCatFathername) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].cat_fathername = newCatFathername;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateCampgnTypename = (index, newCampgnTypename) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].campgn_typename = newCampgnTypename;
      updatedAds[globalIndex].campgn_type = newCampgnTypename === 'sponsored' ? 1 : 2;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateAdbgtname = (index, newAdbgtname) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].adbgtname = newAdbgtname;
      updatedAds[globalIndex].adbgttype = newAdbgtname === 'lifetime' ? 1 : 2;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateAdBgtamt = (index, newAdBgtamt) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].ad_bgtamt = newAdBgtamt;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleUpdateBidname = (index, newBidname) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].bidname = newBidname;
      switch (newBidname) {
        case 'click':
          updatedAds[globalIndex].bidtype = 1;
          break;
        case 'view':
          updatedAds[globalIndex].bidtype = 2;
          break;
        case 'impression':
          updatedAds[globalIndex].bidtype = 3;
          break;
        default:
          updatedAds[globalIndex].bidtype = 1;
      }
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleUpdateBidamount = (index, newBidamount) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].bidamount = newBidamount;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleUpdateAdStartdate = (index, newAdStartdate) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].ad_startdate = newAdStartdate;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleUpdateAdEnddate = (index, newAdEnddate) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].ad_enddate = newAdEnddate;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleUpdateAdStatus = (index, newAdStatus) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].ad_status = newAdStatus;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleUpdateAdApprstatus = (index, newAdApprstatus) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    if (globalIndex >= 0 && globalIndex < ads.length) {
      const updatedAds = [...ads];
      updatedAds[globalIndex].ad_apprstatus = newAdApprstatus;
      setAds(updatedAds);
      setRefreshKey(prev => prev + 1);
    }
  };
  // Handle page size change
  const handlePageSizeChange = () => {
    const newPageSize = parseInt(pageSize);
    if (isNaN(newPageSize) || newPageSize < 1 || newPageSize > 500) {
      showMessage('Page size must be between 1 and 500', 'error');
      setPageSize(50); // Reset to default
      return;
    }

    // Update pagination calculations
    setTotalPages(Math.ceil(ads.length / newPageSize));

    // Reset to first page when changing page size
    setCurrentPage(1);

    // Clear row selection when page size changes
    setSelectedRows(new Set());
    setSelectAll(false);

    showMessage(`Page size set to ${newPageSize} ads per page`, 'success');
  };

  const removeAd = (index) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    const updatedAds = [...ads];
    updatedAds.splice(globalIndex, 1);
    setAds(updatedAds);
    setTotalPages(Math.ceil(updatedAds.length / pageSize));
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  };

  // Row selection functions
  const handleRowSelection = (globalIndex) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(globalIndex)) {
      newSelectedRows.delete(globalIndex);
    } else {
      newSelectedRows.add(globalIndex);
    }
    setSelectedRows(newSelectedRows);
    updateSelectAllState(newSelectedRows);
  };

  const updateSelectAllState = (selectedSet = selectedRows) => {
    const currentPageData = getCurrentPageData();
    const currentPageGlobalIndices = currentPageData.map((_, index) =>
      (currentPage - 1) * pageSize + index
    );
    const allCurrentPageSelected = currentPageGlobalIndices.every(index => selectedSet.has(index));
    setSelectAll(allCurrentPageSelected);
  };

  const handleSelectAll = () => {
    const currentPageData = getCurrentPageData();
    const currentPageGlobalIndices = currentPageData.map((_, index) =>
      (currentPage - 1) * pageSize + index
    );

    const newSelectedRows = new Set(selectedRows);
    const allCurrentPageSelected = currentPageGlobalIndices.every(index => newSelectedRows.has(index));

    if (allCurrentPageSelected) {
      // Deselect all items on current page
      currentPageGlobalIndices.forEach(index => newSelectedRows.delete(index));
    } else {
      // Select all items on current page
      currentPageGlobalIndices.forEach(index => newSelectedRows.add(index));
    }

    setSelectedRows(newSelectedRows);
    setSelectAll(!allCurrentPageSelected);
  };

  // Bulk operation handlers
  const handleBulkCampaignIdChange = () => {
    if (!bulkCampaignId.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];

    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].campaign_id = bulkCampaignId.trim();
      }
    });

    setAds(updatedAds);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied campaign_id "${bulkCampaignId}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkAdTypeNameChange = () => {
    if (!bulkAdTypeName.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];

    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].adtypename = bulkAdTypeName.trim();
        
        // Sync adtype based on adtypename
        switch (bulkAdTypeName.trim()) {
          case 'image':
            updatedAds[globalIndex].adtype = 1;
            break;
          case 'video':
            updatedAds[globalIndex].adtype = 2;
            break;
          case 'product':
            updatedAds[globalIndex].adtype = 3;
            break;
          default:
            updatedAds[globalIndex].adtype = 3; // Default to product
        }
      }
    });

    setAds(updatedAds);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied adtypename "${bulkAdTypeName}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkCampgnTypenameChange = () => {
    if (!bulkCampgnTypename.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];

    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].campgn_typename = bulkCampgnTypename.trim();
        updatedAds[globalIndex].campgn_type = bulkCampgnTypename.trim() === 'sponsored' ? 1 : 2;
      }
    });

    setAds(updatedAds);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied campgn_typename "${bulkCampgnTypename}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkCatIdctgfatheridChange = () => {
    if (!bulkCatIdctgfatherid.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];

    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].cat_idctgfatherid = bulkCatIdctgfatherid.trim();
      }
    });

    setAds(updatedAds);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied cat_idctgfatherid "${bulkCatIdctgfatherid}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkAdbgtnameChange = () => {
    if (!bulkAdbgtname.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];

    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].adbgtname = bulkAdbgtname.trim();
        updatedAds[globalIndex].adbgttype = bulkAdbgtname.trim() === 'lifetime' ? 1 : 2;
      }
    });

    setAds(updatedAds);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied adbgtname "${bulkAdbgtname}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkAdBgtamtChange = () => {
    if (!bulkAdBgtamt.trim()) return;
    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];
    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].ad_bgtamt = bulkAdBgtamt.trim();
      }
    });
    setAds(updatedAds);
    setRefreshKey(prev => prev + 1);
    showMessage(`✅ Applied ad_bgtamt "${bulkAdBgtamt}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkBidnameChange = () => {
    if (!bulkBidname.trim()) return;
    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];
    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].bidname = bulkBidname.trim();
        switch (bulkBidname.trim()) {
          case 'click':
            updatedAds[globalIndex].bidtype = 1;
            break;
          case 'view':
            updatedAds[globalIndex].bidtype = 2;
            break;
          case 'impression':
            updatedAds[globalIndex].bidtype = 3;
            break;
          default:
            updatedAds[globalIndex].bidtype = 1;
        }
      }
    });
    setAds(updatedAds);
    setRefreshKey(prev => prev + 1);
    showMessage(`✅ Applied bidname "${bulkBidname}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkBidamountChange = () => {
    if (!bulkBidamount.trim()) return;
    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];
    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].bidamount = bulkBidamount.trim();
      }
    });
    setAds(updatedAds);
    setRefreshKey(prev => prev + 1);
    showMessage(`✅ Applied bidamount "${bulkBidamount}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkAdStartdateChange = () => {
    if (!bulkAdStartdate.trim()) return;
    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];
    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].ad_startdate = bulkAdStartdate.trim();
      }
    });
    setAds(updatedAds);
    setRefreshKey(prev => prev + 1);
    showMessage(`✅ Applied ad_startdate "${bulkAdStartdate}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkAdEnddateChange = () => {
    if (!bulkAdEnddate.trim()) return;
    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];
    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].ad_enddate = bulkAdEnddate.trim();
      }
    });
    setAds(updatedAds);
    setRefreshKey(prev => prev + 1);
    showMessage(`✅ Applied ad_enddate "${bulkAdEnddate}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkAdStatusChange = () => {
    if (!bulkAdStatus.trim()) return;
    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];
    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].ad_status = bulkAdStatus.trim();
      }
    });
    setAds(updatedAds);
    setRefreshKey(prev => prev + 1);
    showMessage(`✅ Applied ad_status "${bulkAdStatus}" to ${affectedIndices.length} ads`, 'success');
  };

  const handleBulkAdApprstatusChange = () => {
    if (!bulkAdApprstatus.trim()) return;
    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : ads.map((_, index) => index);
    const updatedAds = [...ads];
    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < ads.length) {
        updatedAds[globalIndex].ad_apprstatus = bulkAdApprstatus.trim();
      }
    });
    setAds(updatedAds);
    setRefreshKey(prev => prev + 1);
    showMessage(`✅ Applied ad_apprstatus "${bulkAdApprstatus}" to ${affectedIndices.length} ads`, 'success');
  };

  // Function to display messages
  const showMessage = (message, type = 'success') => {
    setError('');
    if (type === 'success') {
      setSuccessMessage(message);
    } else {
      setError(message);
    }
  };

  const handleUpload = async () => {
    setSuccessMessage('');
    setError('');
    setErrorDetails([]);

    setLoading(true);

    try {
      console.log('🎯 DATABASE: Starting database upload for', ads.length, 'ads');

      // Send to database API
      const response = await axios.post('/api/ads/upload/', {
        ads: ads
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 120000, // 2 minute timeout for large uploads
      });

      console.log('📊 DATABASE: API Response:', response.data);

      if (response.data.success) {
        console.log('✅ DATABASE: Successfully uploaded', response.data.uploaded_count, 'ads to database');

        const successMsg = `✅ Successfully uploaded ${response.data.uploaded_count} ads to database at ${response.data.timestamp}`;
        showMessage(successMsg, 'success');

      } else {
        console.error('❌ DATABASE: Upload failed:', response.data.error);

        // Check if detailed error information is available
        if (response.data.details && Array.isArray(response.data.details) && response.data.details.length > 0) {
          console.error('❌ DATABASE: Detailed errors:', response.data.details);

          // Set main error message and detailed error list separately
          const errorMessage = response.data.error || 'Upload completed with errors';
          setError(errorMessage);
          setErrorDetails(response.data.details);
        } else {
          // Fallback to simple error message
          const errorMsg = response.data.error || 'Upload failed';
          setError(errorMsg);
          setErrorDetails([]);
        }
      }

    } catch (err) {
      console.error('❌ DATABASE: Error during database upload:', err);

      let errorMessage = 'Failed to upload ads to database';
      let details = [];

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
        if (err.response.data.details) {
          details = err.response.data.details;
        }
      }

      setError(errorMessage);
      setErrorDetails(details);
    }

    setLoading(false);
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return ads.slice(startIndex, endIndex);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <button
          key={number}
          className={`mx-1 px-3 py-1 rounded ${
            number === currentPage
              ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
              : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
          }`}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center mt-6">
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          First
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {items}
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          Last
        </button>
      </div>
    );
  };

  if (error && !ads.length) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
          <button
            className="perzsi-button-secondary mt-4"
            onClick={() => navigate('/store-products')}
          >
            Go Back to Store Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className="perzsi-gradient shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <img
                  src="/perzsi-icon-black.png"
                  alt="Perzsi.com"
                  className="h-12 w-auto"
                />
                <p className="text-yellow-100">Ads Upload Table</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-white text-right">
                <p className="text-sm opacity-90">Transformed Ads Data</p>
                <p className="text-xs opacity-75">api_ads Table</p>
              </div>
              <button
                onClick={toggleTheme}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors duration-200"
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 py-8">
        {/* Control Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Ads Upload Table
            </h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Review and modify the transformed ads data before uploading to the database.
            </p>
            <button
              className="perzsi-button-primary w-full"
              onClick={handleUpload}
              disabled={loading || ads.length === 0}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5
2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                `Upload ${ads.length} Ads`
              )}
            </button>

            {successMessage && (
              <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg animate-fade-in">
                <div className="flex items-center">
                  <span className="text-lg mr-2">✅</span>
                  <span className="text-sm font-medium">{successMessage}</span>
                </div>
              </div>
            )}
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Upload Summary
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Data Source:</span>
                <span className="font-semibold">Products</span>
              </div>
              <div className="flex justify-between">
                <span>Total Ads:</span>
                <span className="font-semibold">{ads.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Default Type:</span>
                <span className="font-semibold text-blue-600">PRODUCT</span>
              </div>
              {ads.length > 0 && (
                <div className="flex justify-between">
                  <span>Unique IDs:</span>
                  <span className="font-semibold">{new Set(ads.map(ad => ad.id).filter(Boolean)).size}</span>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t">
              <button
                className="perzsi-button-secondary w-full"
                onClick={() => navigate('/store-products')}
              >
                ← Back to Store Products
              </button>
            </div>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Bulk Operations
            </h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4 text-sm`}>
              Apply changes to selected rows (or all rows if nothing selected):
            </p>

            <div className="grid grid-cols-2 gap-4">
              {/* Campaign ID Bulk Input */}
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Campaign ID"
                  value={bulkCampaignId}
                  onChange={(e) => setBulkCampaignId(e.target.value)}
                  className="perzsi-input flex-1"
                />
                <button
                  onClick={handleBulkCampaignIdChange}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                  disabled={loading}
                >
                  ✓
                </button>
              </div>

              {/* Ad Type Name Bulk Input */}
              <div className="flex gap-2 items-center">
                <select
                  value={bulkAdTypeName}
                  onChange={(e) => setBulkAdTypeName(e.target.value)}
                  className="perzsi-input flex-1"
                >
                  <option value="">Select Ad Type</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="product">Product</option>
                </select>
                <button
                  onClick={handleBulkAdTypeNameChange}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                  disabled={loading}
                >
                  ✓
                </button>
              </div>

              {/* Campaign Type Name Bulk Input */}
              <div className="flex gap-2 items-center">
                <select
                  value={bulkCampgnTypename}
                  onChange={(e) => setBulkCampgnTypename(e.target.value)}
                  className="perzsi-input flex-1"
                >
                  <option value="">Select Campaign Type</option>
                  <option value="sponsored">Sponsored</option>
                  <option value="stroll">Stroll</option>
                </select>
                <button
                  onClick={handleBulkCampgnTypenameChange}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                  disabled={loading}
                >
                  ✓
                </button>
              </div>

              {/* Category ID Father ID Bulk Input */}
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Cat ID Father ID"
                  value={bulkCatIdctgfatherid}
                  onChange={(e) => setBulkCatIdctgfatherid(e.target.value)}
                  className="perzsi-input flex-1"
                />
                <button
                  onClick={handleBulkCatIdctgfatheridChange}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                  disabled={loading}
                >
                  ✓
                </button>
              </div>

              {/* Ad Budget Type Name Bulk Input */}
              <div className="flex gap-2 items-center">
                <select
                  value={bulkAdbgtname}
                  onChange={(e) => setBulkAdbgtname(e.target.value)}
                  className="perzsi-input flex-1"
                >
                  <option value="">Select Ad Budget Type</option>
                  <option value="lifetime">Lifetime</option>
                  <option value="daily">Daily</option>
                </select>
                <button
                  onClick={handleBulkAdbgtnameChange}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                  disabled={loading}
                >
                  ✓
                </button>
              </div>

              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Ad Budget Amount"
                  value={bulkAdBgtamt}
                  onChange={(e) => setBulkAdBgtamt(e.target.value)}
                  className="perzsi-input flex-1"
                />
                <button
                  onClick={handleBulkAdBgtamtChange}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                  disabled={loading}
                >
                  ✓
                </button>
              </div>

              <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} mt-2 col-span-2`}>
                <div>• Select rows in table to apply to specific items</div>
                <div>• Leave empty to apply to all items if no selection</div>
                <div>• Click 📋 to toggle all selection</div>
              </div>
            </div>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Pagination Settings
            </h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Configure how many ads to display per page (1-500).
            </p>

            <div className="space-y-2">
              <div className="flex gap-2 items-center">
                <label htmlFor="pageSize" className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} whitespace-nowrap`}>
                  Page Size:
                </label>
                <input
                  id="pageSize"
                  type="number"
                  min="1"
                  max="500"
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value) || 50)}
                  className="perzsi-input flex-1"
                  placeholder="Enter page size (1-500)"
                />
                <button
                  onClick={handlePageSizeChange}
                  className="perzsi-button-primary"
                  disabled={loading}
                >
                  Apply
                </button>
              </div>

              <div className="text-sm text-gray-600">
                Current: <strong>{pageSize}</strong> ads per page
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="bulkAdStartdate" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="date"
                      id="bulkAdStartdate"
                      placeholder="Ad Start Date"
                      value={bulkAdStartdate}
                      onChange={(e) => setBulkAdStartdate(e.target.value)}
                      className="perzsi-input flex-1"
                      style={{ width: '110px' }}
                    />
                    <button
                      onClick={handleBulkAdStartdateChange}
                      className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                      disabled={loading}
                    >
                      ✓
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="bulkAdEnddate" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="date"
                      id="bulkAdEnddate"
                      placeholder="Ad End Date"
                      value={bulkAdEnddate}
                      onChange={(e) => setBulkAdEnddate(e.target.value)}
                      className="perzsi-input flex-1"
                      style={{ width: '110px' }}
                    />
                    <button
                      onClick={handleBulkAdEnddateChange}
                      className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                      disabled={loading}
                    >
                      ✓
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex gap-2 items-center">
                    <select
                      value={bulkBidname}
                      onChange={(e) => setBulkBidname(e.target.value)}
                      className="perzsi-input flex-1"
                      style={{ width: '100px' }}
                    >
                      <option value="">Select Bid Type</option>
                      <option value="click">Click</option>
                      <option value="view">View</option>
                      <option value="impression">Impression</option>
                    </select>
                    <button
                      onClick={handleBulkBidnameChange}
                      className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                      disabled={loading}
                    >
                      ✓
                    </button>
                </div>
                <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Bid Amount"
                      value={bulkBidamount}
                      onChange={(e) => setBulkBidamount(e.target.value)}
                      className="perzsi-input flex-1"
                    />
                    <button
                      onClick={handleBulkBidamountChange}
                      className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                      disabled={loading}
                    >
                      ✓
                    </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Ad Status"
                      value={bulkAdStatus}
                      onChange={(e) => setBulkAdStatus(e.target.value)}
                      className="perzsi-input flex-1"
                    />
                    <button
                      onClick={handleBulkAdStatusChange}
                      className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                      disabled={loading}
                    >
                      ✓
                    </button>
                </div>
                <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Ad Approval Status"
                      value={bulkAdApprstatus}
                      onChange={(e) => setBulkAdApprstatus(e.target.value)}
                      className="perzsi-input flex-1"
                    />
                    <button
                      onClick={handleBulkAdApprstatusChange}
                      className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                      disabled={loading}
                    >
                      ✓
                    </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <div className="font-semibold mb-2">
              ❌ {error}
            </div>
            {errorDetails && errorDetails.length > 0 && (
              <div className="mt-3 pt-3 border-t border-red-300">
                <div className="text-sm font-medium mb-2 text-red-800">
                  Error Details ({errorDetails.length} issues):
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {errorDetails.map((detail, index) => (
                    <div
                      key={index}
                      className="text-sm bg-red-50 px-2 py-1 rounded border-l-2 border-red-400"
                    >
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Top Pagination */}
        {ads.length > 0 && totalPages > 1 && (
          <div className="mb-4">
            {renderPagination()}
          </div>
        )}

        {/* Ads Upload Table */}
        {ads.length > 0 && (
          <div className="perzsi-table-container">
            <div className="w-full" style={{ overflowX: 'auto', overflowY: 'visible' }}>
              <table className="perzsi-table" style={{ minWidth: '2800px' }}>
                <thead>
                  <tr>
                    <th className="w-16">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-5 h-5 rounded border-2 border-gray-300 text-perzsi-yellow focus:ring-2 focus:ring-perzsi-yellow focus:border-perzsi-yellow cursor-pointer"
                      />
                    </th>
                    <th>adtypename</th>
                    <th>campaign_id</th>
                    <th>campgn_typename</th>
                    <th>adname</th>
                    <th>adtitle</th>
                    <th>addesc</th>
                    <th>adlandingurl</th>
                    <th>aditemurl</th>
                    <th>cat_idctgfatherid</th>
                    <th>cat_fathername</th>
                    <th>adbgtname</th>
                    <th>ad_bgtamt</th>
                    <th>bidname</th>
                    <th>bidamount</th>
                    <th>ad_startdate</th>
                    <th>ad_enddate</th>
                    <th>ad_status</th>
                    <th>ad_apprstatus</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentPageData().map((ad, index) => {
                    const globalIndex = (currentPage - 1) * pageSize + index;

                    return (
                      <tr key={globalIndex} className={`${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${selectedRows.has(globalIndex) ? (isDark ? 'bg-blue-900/20' : 'bg-blue-50') : ''}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRows.has(globalIndex)}
                            onChange={() => handleRowSelection(globalIndex)}
                            className="w-5 h-5 rounded border-2 border-gray-300 text-perzsi-yellow focus:ring-2 focus:ring-perzsi-yellow focus:border-perzsi-yellow cursor-pointer"
                          />
                        </td>
                        <td>
                          <select
                            value={ad.adtypename}
                            onChange={(e) => handleUpdateAdTypeName(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '120px' }}
                          >
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                            <option value="product">Product</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ad.campaign_id}
                            onChange={(e) => handleUpdateCampaignId(index, e.target.value)}
                            placeholder="Enter campaign ID..."
                            className="perzsi-input"
                            style={{ width: '100px' }}
                          />
                        </td>
                        <td>
                          <select
                            value={ad.campgn_typename}
                            onChange={(e) => handleUpdateCampgnTypename(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '120px' }}
                          >
                            <option value="sponsored">Sponsored</option>
                            <option value="stroll">Stroll</option>
                          </select>
                        </td>
                        <td>{ad.adname}</td>
                        <td>
                          <input
                            type="text"
                            value={ad.adtitle}
                            onChange={(e) => handleUpdateAdTitle(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '200px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ad.addesc}
                            onChange={(e) => handleUpdateAdDesc(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '200px' }}
                          />
                        </td>
                        <td>
                          <a
                            href={ad.adlandingurl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline dark:text-blue-300 dark:hover:text-blue-100"
                          >
                            View Link
                          </a>
                        </td>
                        <td>
                          <a
                            href={ad.aditemurl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline dark:text-blue-300 dark:hover:text-blue-100"
                          >
                            View Image
                          </a>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ad.cat_idctgfatherid}
                            onChange={(e) => handleUpdateCatIdctgfatherid(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '100px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ad.cat_fathername}
                            onChange={(e) => handleUpdateCatFathername(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '200px' }}
                          />
                        </td>
                        <td>
                          <select
                            value={ad.adbgtname}
                            onChange={(e) => handleUpdateAdbgtname(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '120px' }}
                          >
                            <option value="lifetime">Lifetime</option>
                            <option value="daily">Daily</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ad.ad_bgtamt}
                            onChange={(e) => handleUpdateAdBgtamt(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td>
                          <select
                            value={ad.bidname}
                            onChange={(e) => handleUpdateBidname(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '130px' }}
                          >
                            <option value="click">Click</option>
                            <option value="view">View</option>
                            <option value="impression">Impression</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ad.bidamount}
                            onChange={(e) => handleUpdateBidamount(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            value={ad.ad_startdate}
                            onChange={(e) => handleUpdateAdStartdate(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '140px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            value={ad.ad_enddate}
                            onChange={(e) => handleUpdateAdEnddate(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '140px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ad.ad_status}
                            onChange={(e) => handleUpdateAdStatus(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ad.ad_apprstatus}
                            onChange={(e) => handleUpdateAdApprstatus(index, e.target.value)}
                            className="perzsi-input"
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td>
                          <button
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors duration-200"
                            onClick={() => removeAd(index)}
                            title="Remove this ad"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {renderPagination()}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            className="perzsi-button-secondary"
            onClick={() => navigate('/store-products')}
          >
            ← Back to Store Products
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdsUpload;
